/** Full creator document contract, shared by cold compilation and persisted writes.
 * Preview serialization deliberately does not enter this guard. Extensions retain
 * their values and precision; known sections only determine canonical field order. */
export type CreatureAppearance = Record<string, unknown>;
export type AppearanceHelmetState = 'closed' | 'open' | 'off';
const MAX_BYTES = 262144, MAX_STRING = 512, MAX_DEPTH = 12;
const sections = ['schema', 'seed', 'rigAbi', 'headSocketAbi', 'body', 'head', 'eyebrows', 'eyelashes', 'makeup',
    'complexion', 'bodyArt', 'hair', 'facialHair', 'surface', 'preview'];
const bytes = (value: string): number => new TextEncoder().encode(value).length;
const record = (value: unknown): value is CreatureAppearance => value !== null && typeof value === 'object' && !Array.isArray(value);

function refuse(reason: string): never { throw new Error(`appearance-document-refused:${reason}`); }

/** Old writers changed hideHelmet alone. Preserve that last-writer interpretation. */
export function appearanceHelmetState(appearance: unknown): AppearanceHelmetState {
    const visibility = record(appearance) && record(appearance.equipmentVisibility) ? appearance.equipmentVisibility : {};
    const state = visibility.helmetState, legacy = visibility.hideHelmet;
    if (state === 'closed' || state === 'open' || state === 'off') {
        return typeof legacy === 'boolean' && legacy !== (state === 'off') ? (legacy ? 'off' : 'closed') : state;
    }
    return legacy === true ? 'off' : 'closed';
}

function checkShape(node: unknown, depth = 0): void {
    if (depth > MAX_DEPTH) refuse('nesting-too-deep');
    if (node === null || typeof node === 'boolean') return;
    if (typeof node === 'number' && Number.isFinite(node)) return;
    if (typeof node === 'string') { if (bytes(node) > MAX_STRING) refuse('string-too-long'); return; }
    if (typeof node !== 'object') refuse('unsupported-value');
    for (const [key, value] of Object.entries(node)) {
        if (bytes(key) > MAX_STRING) refuse('key-too-long');
        checkShape(value, depth + 1);
    }
}

/** Null/absent remains compatible for creatures with no creator document. */
export function creatureAppearanceDocument(raw: unknown): CreatureAppearance | null {
    if (raw == null || raw === false || raw === '') return null;
    if (typeof raw === 'string') {
        raw = raw.trim();
        if (raw === '' || raw === 'null') return null;
        if (bytes(raw as string) > MAX_BYTES) refuse('document-too-large');
        try { raw = JSON.parse(raw as string); } catch { refuse('invalid-json'); }
    }
    if (!record(raw)) refuse('object-required');
    if (typeof raw.schema !== 'string' || !raw.schema.trim()) refuse('schema-required');
    checkShape(raw);
    let equipment = raw.equipmentVisibility;
    if (record(equipment)) {
        const visibility = { ...equipment };
        if (visibility.helmetState === 'closed' || visibility.helmetState === 'open' || visibility.helmetState === 'off' || typeof visibility.hideHelmet === 'boolean') {
            const state = appearanceHelmetState(raw);
            visibility.helmetState = state;
            visibility.hideHelmet = state === 'off';
        } else delete visibility.helmetState;
        equipment = visibility;
    }
    const equipmentOnly = raw.schema === 'ft.creature-equipment-appearance.v1' && record(equipment)
        && (typeof equipment.hideHelmet === 'boolean' || equipment.hoodState === 'up' || equipment.hoodState === 'down');
    if (raw.body == null && raw.head == null && !equipmentOnly) refuse('body-or-head-required');
    for (const section of ['body', 'head']) {
        if (raw[section] != null && typeof raw[section] !== 'object') refuse(`${section}-container-required`);
    }
    const appearanceDoc: CreatureAppearance = {};
    for (const key of new Set([...sections, ...Object.keys(raw)])) {
        if (Object.prototype.hasOwnProperty.call(raw, key)) Object.defineProperty(appearanceDoc, key, {
            value: key === 'equipmentVisibility' ? equipment : raw[key], enumerable: true, writable: true, configurable: true,
        });
    }
    if (bytes(JSON.stringify(appearanceDoc)) > MAX_BYTES) refuse('document-too-large');
    return appearanceDoc;
}

/** Only an intentional persistence operation requires a complete document. */
export function encodeCreatureAppearance(raw: unknown): string {
    const appearanceDoc = creatureAppearanceDocument(raw);
    if (!appearanceDoc) refuse('document-not-ready');
    return JSON.stringify(appearanceDoc);
}
